// src/pages/usa/Utah.js
import "../../App.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import BackOnly from "../../components/BackOnly";
import ContactPopup from "../../components/ContactPopup";
import React, { useState, useEffect } from "react";
import AffiliateBooking from "../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../components/affiliate/AffiliateTrip";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Utah ALWAYS goes back to USA map
export const backRoute = "/map/usa";

export default function Wyoming() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly
  useEffect(() => {
    navigate(".", { state: { backRoute } });
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Wyoming</h1>

        <section className="travel-review">
          {/* LEFT COLUMN */}
          <div className="travel-text">
            <h2>Discover Wyoming</h2>

  <p>
  I’ll be honest — Wyoming wasn’t really on my radar at first. It’s one of those states that quietly sits in the background, often
   overshadowed by its flashier neighbors like Colorado and Utah. But once I started planning my trip, I realized Wyoming has a 
   charm of its own — raw, wild, and surprisingly peaceful.
</p>

<p>
  From the legendary Yellowstone National Park to the dramatic peaks of Grand Teton and the mysterious silhouette of 
  Devils Tower, the state offers a kind of adventure that feels untouched and authentic. It’s the kind of place that doesn’t 
  try to impress you — it just does.
</p>

<p>
  My first visit happened almost by accident. I was planning a trip to South Dakota when I noticed Wyoming was right there,
   practically inviting me to add it to my route. So I did — and Devils Tower absolutely blew me away. The second time, I was
    planning to drive from Idaho to Portland, but at the last minute I changed my mind and headed to Yellowstone instead. Two 
    different trips, two completely different experiences, and both left a mark on me.
</p>
<p>
  Wyoming is also one of the most unique states for travelers. With the lowest population density in the country and nearly 
  half of its land protected as public land, the state feels wide‑open and untouched. Wildlife is everywhere — from bison and 
  elk to pronghorn, which actually outnumber people here. Wyoming is also known for its dramatic weather shifts and some of the
   darkest night skies in the U.S., making it a dream for road‑trippers, photographers, and anyone who loves raw, natural 
   landscapes.
</p>



           <h3>🚗 Getting There</h3>

<p>
  Wyoming is a vast state with multiple entry points depending on where you're headed. 
  For most travelers, the main highlights are Yellowstone National Park, Grand Teton, 
  and Devils Tower — each with its own best routes and nearby airports.
</p>

<p>
  <strong>Flying:</strong> Wyoming has several regional airports, but the two most useful 
  for travelers are <em>Jackson Hole Airport (JAC)</em> — located inside Grand Teton 
  National Park — and <em>Yellowstone Regional Airport (COD)</em> in Cody. Other options 
  include <em>Casper/Natrona County International Airport (CPR)</em> and 
  <em>Gillette–Campbell County Airport (GCC)</em>, which is convenient for visiting 
  Devils Tower. Many travelers also fly into nearby states and drive in, especially 
  from <em>Idaho Falls (IDA)</em>, <em>Salt Lake City (SLC)</em>, or <em>Billings (BIL)</em>.
</p>

<p>
  <strong>Driving:</strong> Wyoming is all about open roads and scenic drives. 
  When I visited Yellowstone, I was staying in Idaho Falls and took <em>US‑20</em>, 
  which is one of the most common and straightforward routes into the park. 
  On another trip, I was staying in South Dakota and drove to Devils Tower using 
  <em>WY‑585</em>, a beautiful and easy drive that takes you straight into the 
  northeastern corner of the state. No matter where you're coming from, expect 
  wide‑open highways, dramatic landscapes, and very little traffic.
</p>

<p>
  <strong>Shuttle Services:</strong> Shuttle options in Wyoming are limited due to the 
  state’s low population density. Some services operate around Jackson, Yellowstone, 
  and Cody, but having a rental car is highly recommended — especially if you want 
  the freedom to explore viewpoints, wildlife areas, and scenic pullouts at your own pace.
</p>


            <h3>🌟 My Personal Observation</h3>

<ul>
  <li>
    <strong>Devils Tower:</strong> The Devils Tower is one of those places that feels like it belongs in a fantasy novel. The 
    towering monolith rises dramatically from the surrounding plains, its sheer cliffs and unique columnar formations creating an
    otherworldly atmosphere. Many Native American tribes have their own stories about the formation of the tower. One of the most
    well‑known legends tells of a giant bear chasing a group of children; as they prayed for help, the ground beneath them rose 
    into the sky, and the bear’s claws left deep vertical marks along the sides of the rock. Other tribes view the tower as a 
    sacred site used for ceremonies, prayer, and vision quests — a place where the physical and spiritual worlds feel especially 
    close.
  </li>

  <li>
  <strong>Yellowstone:</strong> For this one I have to admit that I didn’t plan the right way. I only visited one section of the
  park, and I know there’s so much more to explore. But what I did see was absolutely breathtaking. I entered through US‑191 and 
  ended my visit at West Thumb Geyser Basin. The drive, the geysers, the wildlife — it all felt like stepping into a different 
  world. It was honestly sad to stop and turn back. For sure, I have to return to explore more of the park. I will share some of the
    coordinates of the places I visite that I liked.  Some of them I was not able to go because the parking lot was full, but I
     still got to see them from a distance. I can only imagine how amazing it would be to get up close to some of those geysers
      and hot springs.
  <br /><br />
  <strong>Places I visited:</strong>
  <ul>
    <li>
      <strong>Silex Spring:</strong>
      <a 
        href="https://www.google.com/maps?q=44.550198582541135,-110.80586089860115" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        44.550198582541135, -110.80586089860115
      </a>
    </li>
    <li>
      <strong>Grand Prismatic Spring:</strong>
      <a 
        href="https://www.google.com/maps?q=44.52569185946084,-110.8379450119273" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        44.52569185946084, -110.8379450119273
      </a>
    </li>
    <li>
      <strong>West Thumb Geyser Basin:</strong>
      <a 
        href="https://www.google.com/maps?q=44.41725593161828,-110.57297994528513" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        44.41725593161828, -110.57297994528513
      </a>
    </li>
  </ul>
</li>
</ul>




            {/* HIDDEN GEMS */}
            <h3>🏔️ Hidden Gems in Wyoming</h3>

            <p>
Beyond Yellowstone and Devils Tower, Wyoming is full of places that feel wild, quiet, and untouched. 
  <u>Grand Teton National Park</u> is at the top of my list for a future visit — its jagged peaks, alpine lakes, and wildlife make it one of the most dramatic mountain ranges in the country. 
  The <u>Wind River Range</u> offers glacier‑carved valleys and turquoise lakes that rival any national park. 
  <u>Hot Springs State Park</u> in Thermopolis features colorful mineral terraces and naturally heated soaking pools. 
  <u>Bighorn Canyon</u> delivers towering red‑rock walls and peaceful overlooks without the crowds. 
  Near Lander, the <u>Red Canyon Overlook</u> surprises visitors with deep crimson cliffs and sweeping views. 
  And if you love unique rock formations, <u>Vedauwoo</u> near Laramie is a playground of massive granite boulders perfect for hiking and photography. 
  These spots capture Wyoming’s raw beauty and are perfect additions for anyone planning a deeper exploration of the state.
</p>


            {/* HOTELS */}
           <h3>🛏️ Places to Stay in Wyoming</h3>

<ul>

  
  <li>
    <div className="place-block">
      <strong>Yellowstone Region – Explorer Cabins at Yellowstone (West Yellowstone):</strong>
      Top‑rated cabin‑style lodging with cozy interiors, fireplaces, and a quiet forest setting. 
      Available on Booking.com and Expedia, and located just minutes from the West Entrance.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Yellowstone Region – Kelly Inn West Yellowstone:</strong>
      Well‑rated hotel known for its rustic lodge feel, indoor pool, and excellent location near the park entrance. 
      Listed on both Booking.com and Expedia.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Yellowstone Region – Canyon Lodge & Cabins (Inside the Park):</strong>
      One of the best in‑park options, offering modern rooms and cabins near the Grand Canyon of the Yellowstone. 
      Available on Expedia and often on Booking.com during the season.
    </div>
  </li>


  
  <li>
    <div className="place-block">
      <strong>Devils Tower – Best Western Devils Tower Inn (Hulett):</strong>
      A well‑rated stay with spacious rooms, indoor pool, and a quiet small‑town atmosphere. 
      Available on Booking.com and Expedia, and only 10 minutes from the monument.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Devils Tower – Sundance Mountain Inn (Sundance):</strong>
      Clean, comfortable, and budget‑friendly with great reviews for staff and location. 
      Listed on both Booking.com and Expedia, about 30 minutes from Devils Tower.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Devils Tower – Sawin’ Logs Bed & Breakfast:</strong>
      A top‑rated rustic B&B with homemade breakfast and peaceful prairie views. 
      Available on Expedia and sometimes on Booking.com depending on season.
    </div>
  </li>


  
  <li>
    <div className="place-block">
      <strong>Grand Teton – The Lexington at Jackson Hole:</strong>
      Highly rated boutique hotel with spacious rooms, great service, and walkability to downtown Jackson. 
      Available on Booking.com and Expedia.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Grand Teton – Rustic Inn Creekside Resort & Spa:</strong>
      Luxury cabins and lodge rooms along a private creek, with spa services and beautiful grounds. 
      Listed on both Booking.com and Expedia.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Grand Teton – Teton Mountain Lodge & Spa (Teton Village):</strong>
      Top‑rated resort with mountain views, rooftop hot tubs, and immediate access to the Tetons. 
      Available on Booking.com and Expedia.
    </div>
  </li>

</ul>


<h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
            <div className="affiliate-block">
              <AffiliateExpedia />
              <AffiliateBooking />
              <AffiliateTrip />
            </div>
                <h3>🎥 My Travel Videos</h3>

            <p>I do not have any travel videos to show from Wyoming.</p>

           {/* TODO: Add Black Elk Peak video here
<p>
  <a
    href="https://youtu.be/k4iGWPwe-ts?si=8toy9P6sR21nwt3T"
    target="_blank"
    rel="noopener noreferrer"
  >
    Black Elk Peak
  </a>
</p>
*/}


            
          </div>

{/* RIGHT COLUMN */}
<div className="travel-image-column">
  <img src="/images/usa/wyoming/14.jpg" alt="Yellowstone" />
   <img src="/images/usa/wyoming/2.jpg" alt="Yellowstone" />
    <img src="/images/usa/wyoming/3.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/4.jpg" alt="Yellowstone" />
    <img src="/images/usa/wyoming/5.jpg" alt="Yellowstone" />
   <img src="/images/usa/wyoming/6.jpg" alt="Yellowstone" />
    <img src="/images/usa/wyoming/7.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/8.jpg" alt="Yellowstone" /> 
     <img src="/images/usa/wyoming/1.jpg" alt="Yellowstone" />
   <img src="/images/usa/wyoming/10.jpg" alt="Yellowstone" />
    <img src="/images/usa/wyoming/11.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/12.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/13.jpg" alt="Yellowstone" />
   <img src="/images/usa/wyoming/15.jpg" alt="Yellowstone" />
    <img src="/images/usa/wyoming/16.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/17.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/18.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/19.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/20.jpg" alt="Yellowstone" />
   <img src="/images/usa/wyoming/21.jpg" alt="Yellowstone" />
    <img src="/images/usa/wyoming/22.jpg" alt="Yellowstone" />
     <img src="/images/usa/wyoming/23.jpg" alt="Yellowstone" /> 
     <img src="/images/usa/wyoming/devilstower.jpg" alt="Devils Tower" />
     <img src="/images/usa/wyoming/dt2.jpg" alt="Devils Tower" /> 
      <img src="/images/usa/wyoming/dt3.jpg" alt="Devils Tower" />
        <img src="/images/usa/wyoming/dtf.jpg" alt="Devils Tower" />
         <img src="/images/usa/wyoming/dtov.jpg" alt="Devils Tower" />
          <img src="/images/usa/wyoming/dtt.jpg" alt="Devils Tower" />


  
  
</div>
</section>
</div>

<div className="email-wrapper">
  <button className="email-link" onClick={() => setOpen(true)}>
    contact@thefindingspot.com
  </button>
</div>

<ContactPopup isOpen={open} onClose={() => setOpen(false)} />

<Footer />
</>
);
}
